// FeedbackCenter.jsx — the header "Feedback" button and everything behind it.
//
// Send: capture this screen (or paste/upload an image), drag to highlight the
// part that matters, describe the change, send. The API files it as a GitHub
// issue; Claude Code proposes a fix there; an approver approves here (or on
// GitHub); Claude Code builds it on its own branch and opens a PR into Feature.
//
// Track: "My feedback" lists what you sent and where it stands, read off the
// GitHub issue by the API. Approvers also get "All feedback", Claude's proposed
// plan, and Approve / Ask for changes / Decline.
import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../api/client';
import { B, font, radius, space, weight } from '../constants/brand';
import { Btn, Modal, ModalFooter, Textarea, Tabs, Spinner, EmptyState, Icon, useToast } from '../components/ui';
import { canCaptureScreen, captureScreen, snapshotPage, SNAPSHOT_HINT, imageFileToDataUrl, drawHighlights, boxFromDrag } from './capture';
import { snapshotDiagnostics } from './diagnostics';
import { HeaderIconButton } from '../components/shared/HeaderIconButton';

const STATUS_TONE = {
  submitted: B.textMd, received: B.tealDk, 'needs-info': '#b45309', planned: '#b45309',
  building: B.navy, 'in-review': B.navy, shipped: '#15803d', declined: B.textLt, stuck: B.red, error: B.red,
};

// The header is a blurred (backdrop-filter) bar, and a fixed-position overlay
// inside it is positioned against the header, not the window — the dialog was
// clipped to a 60px strip with its close button out of reach. Render it on
// <body> instead.
const toBody = (node) => (typeof document !== 'undefined' ? createPortal(node, document.body) : node);

const STEPS_FALLBACK = ['Sent', 'Claude plans a fix', 'Approved', 'Claude builds it', 'Reviewed & shipped'];

// Where this piece of feedback is in its journey. `step` is how many steps are
// done; the next one is the current one.
function Progress({ steps, step, status }) {
  const list = steps && steps.length ? steps : STEPS_FALLBACK;
  if (status === 'declined') return null;
  const bad = status === 'stuck' || status === 'error';
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
      {list.map((label, i) => {
        const done = i < step;
        const current = i === step;
        const color = done ? B.tealDk : current ? (bad ? B.red : B.navy) : B.border;
        return (
          <div key={label} style={{ flex: 1, minWidth: 0 }}>
            <div style={{ height: 4, borderRadius: 2, background: color, opacity: current && !bad ? 0.6 : 1 }} />
            <div style={{ fontSize: font.xs, marginTop: 4, color: done || current ? B.text : B.textLt, fontWeight: current ? weight.semibold : weight.regular, display: 'flex', alignItems: 'center', gap: 3 }}>
              {done && <Icon name="check" size={12} />}{label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NextStep({ text }) {
  if (!text) return null;
  return (
    <div style={{ fontSize: font.sm, color: B.text, background: B.surfaceMuted, border: `1px solid ${B.divider}`, borderRadius: radius.md, padding: `${space.sm}px ${space.md}px` }}>
      <span style={{ fontWeight: weight.semibold }}>What happens next: </span>{text}
    </div>
  );
}

function StatusPill({ status, words }) {
  const color = STATUS_TONE[status] || B.textMd;
  return (
    <span style={{ display: 'inline-block', fontSize: font.xs, fontWeight: weight.semibold, color, border: `1px solid ${color}`, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>
      {words || status}
    </span>
  );
}

const when = (s) => (s ? new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

// ---------------------------------------------------------------------------
// Screenshot with drag-to-highlight
// ---------------------------------------------------------------------------
function Highlighter({ src, boxes, onBoxes }) {
  const imgRef = useRef(null);
  const [drag, setDrag] = useState(null);

  const toImage = (e) => {
    const img = imgRef.current;
    const r = img.getBoundingClientRect();
    const sx = img.naturalWidth / r.width;
    return { x: (e.clientX - r.left) * sx, y: (e.clientY - r.top) * sx, scale: sx };
  };
  const onDown = (e) => { e.preventDefault(); const p = toImage(e); setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y }); };
  const onMove = (e) => { if (!drag) return; const p = toImage(e); setDrag((d) => ({ ...d, x1: p.x, y1: p.y })); };
  const onUp = () => {
    if (!drag) return;
    const b = boxFromDrag(drag.x0, drag.y0, drag.x1, drag.y1);
    if (b) onBoxes([...boxes, b]);
    setDrag(null);
  };

  const img = imgRef.current;
  const scale = img && img.naturalWidth ? img.getBoundingClientRect().width / img.naturalWidth : 1;
  const live = drag ? boxFromDrag(drag.x0, drag.y0, drag.x1, drag.y1, 1) : null;
  const shown = live ? [...boxes, live] : boxes;

  return (
    <div style={{ position: 'relative', border: `1px solid ${B.border}`, borderRadius: radius.md, overflow: 'hidden', cursor: 'crosshair', userSelect: 'none' }}
      onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}>
      <img ref={imgRef} src={src} alt="Screenshot to send" draggable={false} style={{ display: 'block', width: '100%' }} />
      {shown.map((b, i) => (
        <div key={i} style={{ position: 'absolute', left: b.x * scale, top: b.y * scale, width: b.w * scale, height: b.h * scale, border: '3px solid #e11d48', borderRadius: 2, pointerEvents: 'none' }} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Send
// ---------------------------------------------------------------------------
function SendForm({ page, onHide, onSent, onCancel }) {
  const { toast } = useToast();
  const [shot, setShot] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  // The context is taken when the dialog opens, before anything in it can
  // add errors of its own.
  const ctxRef = useRef(snapshotDiagnostics({ page }));

  const takeFile = useCallback(async (file) => {
    try { setShot(await imageFileToDataUrl(file)); setBoxes([]); setError(null); }
    catch (e) { setError(e.message); }
  }, []);

  useEffect(() => {
    const onPaste = (e) => {
      const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith('image/'));
      if (item) { e.preventDefault(); takeFile(item.getAsFile()); }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [takeFile]);

  // `exact` uses the browser's screen share (it asks first); the default is the
  // quick snapshot, which needs no prompt.
  const capture = async (exact = false) => {
    setError(null); setCapturing(true);
    onHide(true);
    // Two frames so the dialog is actually gone from the screen before the copy.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try { setShot(await (exact ? captureScreen() : snapshotPage())); setBoxes([]); }
    catch (e) {
      if (e && e.name === 'NotAllowedError') return;
      setError(exact
        ? `Could not capture the screen: ${e.message || e}`
        : `Could not take a snapshot of this page: ${e.message || e}. Paste or upload a screenshot instead.`);
    }
    finally { onHide(false); setCapturing(false); }
  };

  const send = async () => {
    setBusy(true); setError(null);
    try {
      const screenshot = shot ? await drawHighlights(shot, boxes) : null;
      const res = await api.post('feedback', { description: text, screenshot, context: ctxRef.current }, { timeout: 60000 });
      toast(res.sent ? 'Thanks — your feedback is with Claude.' : `Saved. It will be sent to GitHub shortly${res.note ? ` (${res.note})` : ''}.`);
      onSent(res.feedback);
    } catch (e) {
      setError(e.message || 'Could not send your feedback.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
      <div style={{ fontSize: font.sm, color: B.textMd }}>
        Show us the screen and say what you would like changed, fixed or added.
      </div>
      <Progress steps={STEPS_FALLBACK} step={0} status="submitted" />
      <div style={{ fontSize: font.xs, color: B.textLt, marginTop: -space.sm }}>
        After you send it, Claude reads the code and proposes a fix (a few minutes). An approver reviews it; once approved, Claude builds it and a person reviews the change before it goes live. You can follow it under My feedback.
      </div>

      {shot ? (
        <div>
          <Highlighter src={shot} boxes={boxes} onBoxes={setBoxes} />
          <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', marginTop: space.sm, flexWrap: 'wrap' }}>
            <span style={{ fontSize: font.xs, color: B.textLt }}>Drag on the picture to circle the part you mean.</span>
            <span style={{ flex: 1 }} />
            {boxes.length > 0 && <Btn size="sm" variant="ghost" onClick={() => setBoxes([])}>Clear highlights</Btn>}
            <Btn size="sm" variant="ghost" onClick={() => { setShot(null); setBoxes([]); }}>Remove screenshot</Btn>
          </div>
        </div>
      ) : (
        <div style={{ border: `1px dashed ${B.border}`, borderRadius: radius.lg, padding: space.xl, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: space.md, background: B.surfaceMuted }}>
          <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Btn onClick={() => capture(false)} loading={capturing}><Icon name="camera" size={15} /> Capture this screen</Btn>
            <Btn variant="secondary" onClick={() => fileRef.current?.click()}><Icon name="image" size={15} /> Upload an image</Btn>
          </div>
          <div style={{ fontSize: font.xs, color: B.textLt, textAlign: 'center' }}>
            {SNAPSHOT_HINT}
          </div>
          {canCaptureScreen() && (
            <button type="button" onClick={() => capture(true)} disabled={capturing}
              style={{ background: 'none', border: 'none', padding: 0, color: B.navy, fontSize: font.xs, textDecoration: 'underline', cursor: capturing ? 'default' : 'pointer' }}>
              Exact capture (browser asks first)
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) takeFile(f); }} />
        </div>
      )}

      <Textarea label="What would you like changed?" value={text} onChange={setText} rows={5} required
        placeholder="e.g. The Total Cost column should show the debt beside it, or: Saving a task on this screen spins forever." />

      <div style={{ fontSize: font.xs, color: B.textLt }}>
        Sent with it: the page you are on, your browser, and any errors this tab recorded recently. Screenshots may show deal data; they are kept in our private GitHub repository.
      </div>

      {error && <div style={{ color: B.red, fontSize: font.sm }}>{error}</div>}
      <ModalFooter>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
        <Btn onClick={send} loading={busy} disabled={!text.trim() || busy}><Icon name="send" size={15} /> Send feedback</Btn>
      </ModalFooter>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------
function FeedbackDetail({ id, canSeeIssue, onClose, onChanged }) {
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [mode, setMode] = useState(null); // 'reply' | 'approve' | 'decline'
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setErr(null);
    api.get(`feedback/${encodeURIComponent(id)}`, { timeout: 30000 }).then(setD).catch((e) => setErr(e.message));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // While Claude is working, look again every 20 seconds so the plan appears
  // without closing and reopening.
  useEffect(() => {
    if (!d || !['received', 'building', 'submitted'].includes(d.status)) return undefined;
    const t = setTimeout(load, 20000);
    return () => clearTimeout(t);
  }, [d, load]);

  const retry = async () => {
    setBusy(true);
    try {
      await api.post(`feedback/${encodeURIComponent(id)}/retry`, {}, { timeout: 30000 });
      toast('Running Claude again.');
      load(); onChanged();
    } catch (e) { toast(e.message || 'That did not go through.', 'error'); }
    finally { setBusy(false); }
  };

  const act = async () => {
    setBusy(true);
    try {
      const path = `feedback/${encodeURIComponent(id)}/${mode}`;
      const body = mode === 'reply' ? { text } : mode === 'approve' ? { note: text } : { reason: text };
      await api.post(path, body, { timeout: 30000 });
      toast(mode === 'approve' ? 'Approved — Claude is building it.' : mode === 'decline' ? 'Marked not planned.' : 'Sent — Claude will look again.');
      setMode(null); setText(''); load(); onChanged();
    } catch (e) { toast(e.message || 'That did not go through.', 'error'); }
    finally { setBusy(false); }
  };

  // Claude asked a question: its comment is a question, not a plan to approve.
  const asking = !!d && d.status === 'needs-info';

  return toBody(
    <Modal title="Feedback" onClose={onClose} width={760}>
      {err && <div style={{ color: B.red }}>{err}</div>}
      {!d && !err && <Spinner />}
      {d && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusPill status={d.status} words={d.statusWords} />
            <span style={{ fontSize: font.xs, color: B.textLt }}>{d.submittedBy} · {when(d.createdUTC)}{d.pageLabel ? ` · ${d.pageLabel}` : ''}</span>
            <span style={{ flex: 1 }} />
            {canSeeIssue && d.issueUrl && <a href={d.issueUrl} target="_blank" rel="noreferrer" style={{ fontSize: font.xs }}>Issue #{d.issueNumber}</a>}
            {canSeeIssue && d.prUrl && <a href={d.prUrl} target="_blank" rel="noreferrer" style={{ fontSize: font.xs }}>Pull request</a>}
          </div>
          <Progress steps={d.steps} step={d.step} status={d.status} />
          <NextStep text={d.nextStep} />
          <div style={{ whiteSpace: 'pre-wrap', fontSize: font.sm, color: B.text }}>{d.description}</div>
          {d.screenshotUrl && <img src={d.screenshotUrl} alt="Screenshot" style={{ width: '100%', border: `1px solid ${B.border}`, borderRadius: radius.md }} />}
          {d.error && <div style={{ color: B.red, fontSize: font.sm }}>Not sent to GitHub yet: {d.error}</div>}

          <div>
            <div style={{ fontWeight: weight.semibold, fontSize: font.sm, marginBottom: space.xs }}>{asking ? "Claude's question" : "Claude's proposed fix"}</div>
            {d.plan ? (
              <div style={{ whiteSpace: 'pre-wrap', fontSize: font.sm, background: B.surfaceMuted, border: `1px solid ${B.divider}`, borderRadius: radius.md, padding: space.md, maxHeight: 360, overflowY: 'auto' }}>
                {d.plan.body}
              </div>
            ) : (
              <div style={{ fontSize: font.sm, color: B.textLt }}>
                {d.status === 'stuck' ? 'Claude did not finish — press Try again.'
                  : d.issueNumber ? 'Not written yet — Claude usually answers within a few minutes. This page checks again every 20 seconds.'
                  : 'Waiting for the feedback to reach GitHub.'}
              </div>
            )}
          </div>

          {mode && (
            <Textarea
              label={mode === 'reply' ? (asking ? 'Your answer' : d.canApprove ? 'What should change in the plan?' : 'What would you like to add?') : mode === 'approve' ? 'Note for Claude (optional)' : 'Why not? (optional)'}
              value={text} onChange={setText} rows={4} />
          )}
          <ModalFooter>
            {mode ? (
              <>
                <Btn variant="ghost" onClick={() => { setMode(null); setText(''); }}>Cancel</Btn>
                <Btn onClick={act} loading={busy} disabled={busy || (mode === 'reply' && !text.trim())} variant={mode === 'decline' ? 'danger' : 'primary'}>
                  {mode === 'approve' ? 'Approve and build' : mode === 'decline' ? 'Decline' : 'Send'}
                </Btn>
              </>
            ) : (
              <>
                <Btn variant="ghost" onClick={onClose}>Close</Btn>
                {d.canRetry && <Btn variant="secondary" onClick={retry} loading={busy}><Icon name="refresh" size={14} /> Try again</Btn>}
                {d.canReply && <Btn variant={asking ? 'primary' : 'secondary'} onClick={() => setMode('reply')}>{asking ? 'Answer question' : d.canApprove ? 'Ask for changes' : 'Add detail'}</Btn>}
                {d.canDecline && <Btn variant="ghost" onClick={() => setMode('decline')}>Decline</Btn>}
                {d.canApprove && !asking && <Btn onClick={() => setMode('approve')} disabled={!d.plan}>Approve</Btn>}
              </>
            )}
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------
function FeedbackList({ scope, onOpen, refreshKey, onMeta }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setData(null); setErr(null);
    api.get(`feedback?scope=${scope}`, { timeout: 30000 })
      .then((r) => { setData(r); onMeta && onMeta(r); })
      .catch((e) => setErr(e.message));
  }, [scope, refreshKey]);
  if (err) return <div style={{ color: B.red }}>{err}</div>;
  if (!data) return <Spinner />;
  if (!data.available) return <EmptyState title="Feedback is not set up yet" message="The database migration for feedback has not run." />;
  if (!data.items.length) return <EmptyState icon="message" title="Nothing yet" message="Feedback you send shows up here with where it stands." />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {data.items.map((f) => (
        <button key={f.feedbackId} type="button" onClick={() => onOpen(f.feedbackId)}
          style={{ textAlign: 'left', background: 'none', border: 'none', borderBottom: `1px solid ${B.divider}`, padding: `${space.md}px 0`, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', gap: space.md, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: font.sm, color: B.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.description}</div>
            <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 2 }}>
              {scope === 'all' ? `${f.submittedBy} · ` : ''}{when(f.createdUTC)}{f.pageLabel ? ` · ${f.pageLabel}` : ''}
            </div>
          </div>
          <StatusPill status={f.status} words={f.statusWords} />
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header button + dialog
// ---------------------------------------------------------------------------
export default function FeedbackCenter({ page }) {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [tab, setTab] = useState('send');
  const [detailId, setDetailId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [meta, setMeta] = useState(null);

  // Learn once whether this person may see everyone's feedback.
  useEffect(() => {
    if (!open || meta) return;
    api.get('feedback?scope=mine', { timeout: 30000 }).then(setMeta).catch(() => {});
  }, [open]);

  // Tabs takes its labels as its ids.
  const TAB_IDS = { 'Send feedback': 'send', 'My feedback': 'mine', 'All feedback': 'all' };
  const tabs = ['Send feedback', 'My feedback', ...(meta && meta.canSeeAll ? ['All feedback'] : [])];
  const tabLabel = Object.keys(TAB_IDS).find((k) => TAB_IDS[k] === tab);

  return (
    <>
      <HeaderIconButton icon="message" label="Send feedback"
        onClick={() => { setOpen(true); setTab('send'); }} />

      {/* Hidden during a capture with CSS, not unmounted: unmounting would
          throw away the form (and the picture being taken). data-feedback-skip
          also keeps it out of a quick snapshot. */}
      {open && toBody(
        <div data-feedback-skip="" style={hidden ? { display: 'none' } : undefined}>
        <Modal title="Feedback" onClose={() => setOpen(false)} width={760}>
          <Tabs tabs={tabs} active={tabLabel} onChange={(label) => setTab(TAB_IDS[label])} />
          <div style={{ marginTop: space.lg }}>
            {tab === 'send' && (
              <SendForm page={page} onHide={setHidden} onCancel={() => setOpen(false)}
                onSent={(fb) => { setRefreshKey((k) => k + 1); setTab('mine'); if (fb && fb.feedbackId) setDetailId(fb.feedbackId); }} />
            )}
            {tab !== 'send' && (
              <FeedbackList scope={tab} refreshKey={refreshKey} onOpen={setDetailId} onMeta={(m) => setMeta((p) => p || m)} />
            )}
          </div>
        </Modal>
        </div>
      )}

      {detailId && (
        <FeedbackDetail id={detailId} canSeeIssue={!!(meta && meta.canSeeAll)}
          onClose={() => setDetailId(null)} onChanged={() => setRefreshKey((k) => k + 1)} />
      )}
    </>
  );
}
